<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";

import HelpCenterAskPanel from "@/components/HelpCenterAskPanel.vue";
import JobAnalysisToastStack from "@/components/JobAnalysisTracker/JobAnalysisToastStack.vue";
import OnboardingTour from "@/components/OnboardingTour.vue";
import { routeContentKey } from "@/utils/route_key";
import { useAuth } from "@/utils/authentication";
import AuthenticationStatus from "@/components/AuthenticationStatus.vue";

const route = useRoute();
const router = useRouter();
const { status, session } = useAuth();
const isRouteLoading = ref(false);
let loadingTimer: ReturnType<typeof setTimeout> | undefined;
let pendingPath: string | undefined;

const stopRouteLoading = () => {
  clearTimeout(loadingTimer);
  loadingTimer = undefined;
  pendingPath = undefined;
  isRouteLoading.value = false;
};

const removeBeforeEach = router.beforeEach((to, from) => {
  stopRouteLoading();
  if (
    routeContentKey(to.fullPath) === routeContentKey(from.fullPath) ||
    !to.matched.some(
      (record) => typeof record.components?.default === "function",
    )
  )
    return;
  pendingPath = to.fullPath;
  loadingTimer = setTimeout(() => {
    isRouteLoading.value = true;
  }, 150);
});
const removeAfterEach = router.afterEach((to) => {
  if (to.fullPath === pendingPath) stopRouteLoading();
});
const removeOnError = router.onError(stopRouteLoading);

onUnmounted(() => {
  stopRouteLoading();
  removeBeforeEach();
  removeAfterEach();
  removeOnError();
});
const showAuthenticationStatus = computed(
  () =>
    !["Login", "Logout"].includes(String(route.name)) &&
    status.value !== "ready" &&
    (status.value !== "signedOut" ||
      !route.matched.some((record) => record.meta.public === true)),
);
// Hash-only navigation must not remount the route component (Help Center TOC anchors).
const routeKey = computed(
  () =>
    `${session.value?.generation ?? "anonymous"}:${routeContentKey(route.fullPath)}`,
);
</script>

<template>
  <AuthenticationStatus v-if="showAuthenticationStatus" />
  <RouterView v-else :key="routeKey" />
  <!-- Mounted once at the app root (not inside a page's Layout) so the conversation and the
  panel's open state survive route navigation instead of resetting on every page. -->
  <HelpCenterAskPanel v-if="status === 'ready'" :key="session?.generation" />
  <OnboardingTour v-if="status === 'ready'" :key="session?.generation" />
  <JobAnalysisToastStack v-if="status === 'ready'" :key="session?.generation" />
  <div
    v-if="isRouteLoading"
    class="route-loading-overlay"
    role="status"
    aria-label="Loading page"
  />
</template>

<style scoped lang="scss">
@use "@/assets/styles/design-tokens" as *;

.route-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 14040;
  background-color: $color-content-subtle;
  animation: route-loading-pulse 1.6s ease-in-out infinite;
}

@keyframes route-loading-pulse {
  0%,
  100% {
    opacity: 0.18;
  }

  50% {
    opacity: 0.32;
  }
}

@media (prefers-reduced-motion: reduce) {
  .route-loading-overlay {
    animation: none;
    opacity: 0.25;
  }
}
</style>
